import styled from "@emotion/styled";
import { CoreViewer, Navigation, PageViewMode, ReadyState, } from "@vivliostyle/core";
import React, { useEffect, useRef } from "react";
import { epageFromPageNumber } from "./epage";
export const Renderer = ({ source, page = 1, zoom = 1, bookMode = true, fontSize = 16, background = "#ececec", renderAllPages = true, autoResize = true, pageViewMode = PageViewMode.SINGLE_PAGE, defaultPaperSize, pageBorderWidth = 1, fitToScreen = false, userStyleSheet, authorStyleSheet, style, onMessage, onError, onReadyStateChange, onLoad, onNavigation, onHyperlink, children, }) => {
    const containerRef = useRef(null);
    const instanceRef = useRef(undefined);
    const stateRef = React.useRef(undefined);
    // Use a key to force re-creation of the container element on re-mount (e.g., in StrictMode)
    const [containerKey, setContainerKey] = React.useState(0);
    function setViewerOptions() {
        const viewerOptions = {
            fontSize,
            pageViewMode,
            renderAllPages,
            autoResize,
            defaultPaperSize,
            pageBorderWidth,
        };
        instanceRef.current.setOptions(viewerOptions);
    }
    function loadSource() {
        const instance = instanceRef.current;
        const documentOptions = Object.assign(Object.assign({}, (userStyleSheet
            ? {
                userStyleSheet: [
                    {
                        [userStyleSheet.endsWith(".css") ? "url" : "text"]: userStyleSheet,
                    },
                ],
            }
            : null)), (authorStyleSheet
            ? {
                authorStyleSheet: [
                    {
                        [authorStyleSheet.endsWith(".css") ? "url" : "text"]: authorStyleSheet,
                    },
                ],
            }
            : null));
        if (bookMode) {
            instance.loadPublication(source, documentOptions);
        }
        else {
            instance.loadDocument({ url: source }, documentOptions, {
                fontSize,
                pageViewMode,
                zoom,
                renderAllPages,
                autoResize,
                defaultPaperSize,
                pageBorderWidth,
                fitToScreen,
            });
        }
    }
    function registerEventHandlers() {
        const getMessage = (payload) => { var _a, _b; return (_b = (_a = payload.content.error) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : payload.content.messages.join("\n"); };
        function handleMessage(payload, type) {
            onMessage && onMessage(getMessage(payload), type);
        }
        const handleDebug = (payload) => handleMessage(payload, "debug");
        const handleInfo = (payload) => handleMessage(payload, "info");
        const handleWarn = (payload) => handleMessage(payload, "warn");
        function handleError(payload) {
            onError && onError(getMessage(payload));
        }
        function handleReadyStateChange() {
            const { readyState } = instanceRef.current;
            onReadyStateChange && onReadyStateChange(readyState);
        }
        function handleLoaded() {
            onLoad && onLoad(stateRef.current);
        }
        function handleNavigation(payload) {
            const { docTitle, epageCount, epage, metadata } = payload;
            const currentState = {
                docTitle,
                epageCount,
                epage: epage,
                metadata,
            };
            stateRef.current = currentState;
            onNavigation && onNavigation(currentState);
        }
        function handleHyperlink(payload) {
            onHyperlink && onHyperlink(payload);
        }
        const instance = instanceRef.current;
        instance.addListener("debug", handleDebug);
        instance.addListener("info", handleInfo);
        instance.addListener("warn", handleWarn);
        instance.addListener("error", handleError);
        instance.addListener("readystatechange", handleReadyStateChange);
        instance.addListener("loaded", handleLoaded);
        instance.addListener("nav", handleNavigation);
        instance.addListener("hyperlink", handleHyperlink);
        return () => {
            onReadyStateChange && onReadyStateChange(ReadyState.LOADING);
            instance.removeListener("debug", handleDebug);
            instance.removeListener("info", handleInfo);
            instance.removeListener("warn", handleWarn);
            instance.removeListener("error", handleError);
            instance.removeListener("readystatechange", handleReadyStateChange);
            instance.removeListener("loaded", handleLoaded);
            instance.removeListener("nav", handleNavigation);
            instance.removeListener("hyperlink", handleHyperlink);
        };
    }
    function initInstance() {
        instanceRef.current = new CoreViewer({
            viewportElement: containerRef.current,
        });
    }
    // initialize document and event handlers
    useEffect(() => {
        // Increment key to force new container element, ensuring previous CoreViewer's
        // async rendering won't affect the new container (handles StrictMode double-mount)
        setContainerKey((k) => k + 1);
    }, []);
    useEffect(() => {
        initInstance();
        setViewerOptions();
        const cleanup = registerEventHandlers();
        return cleanup;
    }, [containerKey]);
    useEffect(() => {
        if (containerKey === 0)
            return; // Skip initial render before key is set
        loadSource();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        containerKey,
        source,
        authorStyleSheet,
        userStyleSheet,
        zoom,
        fitToScreen,
        bookMode,
    ]);
    useEffect(() => {
        setViewerOptions();
    }, [
        fontSize,
        pageViewMode,
        renderAllPages,
        autoResize,
        defaultPaperSize,
        pageBorderWidth,
    ]);
    // sync location
    useEffect(() => {
        var _a;
        const epage = epageFromPageNumber(page);
        (_a = instanceRef.current) === null || _a === void 0 ? void 0 : _a.navigateToPage(Navigation.EPAGE, epage);
    }, [page]);
    const container = (React.createElement(Container, { key: containerKey, ref: containerRef, style: style, background: background }));
    if (typeof children === "function" && children instanceof Function) {
        return children({ container, reload: loadSource });
    }
    return container;
};
const Container = styled.div `
  overflow: scroll;
  background: ${({ background }) => background} !important;
`;
//# sourceMappingURL=renderer.js.map